import { FormEvent, useMemo, useState } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import isEmail from 'validator/lib/isEmail'
import { Button, Header, TextInput } from 'components/common'
import { axiosBase } from 'utils/functions'
import PublicPageContainer from './PublicPageContainer'
import './AuthPages.scss'

interface ResetRouteParams {
  token?: string
}

function flattenMessageValue(value: unknown): string {
  if (typeof value === 'string') return value

  if (Array.isArray(value)) {
    return value.map(flattenMessageValue).join(', ')
  }

  if (value && typeof value === 'object') {
    return Object.values(value).map(flattenMessageValue).join(', ')
  }

  return String(value)
}

function toErrorMessage(error: unknown): string {
  const message = axios.isAxiosError(error)
    ? error.response?.data?.message
    : undefined

  if (typeof message === 'string') return message

  if (Array.isArray(message) || (message && typeof message === 'object')) {
    return flattenMessageValue(message)
  }

  return 'Something went wrong. Please try again.'
}

function ForgotResetPasswordPage() {
  const { t } = useTranslation('pub', {
    keyPrefix: 'PublicRoutes.auth',
  })
  const { t: tSignUp } = useTranslation('pub', {
    keyPrefix: 'PublicRoutes.signUp',
  })
  const { token } = useParams<ResetRouteParams>()

  const history = useHistory()
  const isResetFlow = useMemo(() => Boolean(token), [token])
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [error, setError] = useState('')
  const [passwordTooShort, setPasswordTooShort] = useState(false)
  const [success, setSuccess] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const PASSWORD_MIN_LENGTH = 15

  const handleForgotPassword = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError('')
    setSuccess('')

    if (!isEmail(email)) {
      setError(t('login.invalid'))
      return
    }

    setIsSubmitting(true)

    try {
      await axiosBase.post('/v2/users/password', {
        email,
        application: 'my-cyber-risk',
        customer_type: 'mcr',
      })

      setSuccess(t('forgotPassword.emailSent'))
    } catch (_error) {
      setError(t('forgotPassword.emailSent'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleResetPassword = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError('')
    setPasswordTooShort(false)

    if (!token) {
      setError(t('forgotPassword.invalidToken'))
      return
    }

    if (password.length < PASSWORD_MIN_LENGTH) {
      setPasswordTooShort(true)
      return
    }

    if (password !== passwordConfirmation) {
      setError(tSignUp('invalidPasswordConfirm'))
      return
    }

    setIsSubmitting(true)

    try {
      await axiosBase.put('/v2/users/password', {
        reset_password_token: token,
        password,
        password_confirmation: passwordConfirmation,
      })

      history.push('/login')
    } catch (requestError) {
      setError(toErrorMessage(requestError))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <PublicPageContainer>
      <div className="auth-page">
        <div className="auth-card">
          <Header as="h3" className="auth-title">
            {isResetFlow
              ? t('passwordReset.resetPassword')
              : t('all.forgotPassword')}
          </Header>

          <div className="auth-subtitle">
            {t('passwordReset.resetPasswordText')}
          </div>

          {isResetFlow ? (
            <form
              className="auth-form"
              onSubmit={handleResetPassword}
              noValidate
            >
              {passwordTooShort ? (
                <div className="auth-error password-requirements">
                  <div>{t('passwordReset.passwordRequirements')}</div>
                  <ul>
                    <li>{t('passwordReset.minLength')}</li>
                  </ul>
                </div>
              ) : null}

              <TextInput
                id="reset-password"
                type="password"
                label={t('passwordReset.newPassword')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                fullBorder
              />

              <TextInput
                id="reset-password-confirmation"
                type="password"
                label={t('passwordReset.confirmPassword')}
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
                autoComplete="new-password"
                fullBorder
                error={error}
              />

              <Button color="blue" type="submit" loading={isSubmitting}>
                {t('all.continue')}
              </Button>
            </form>
          ) : (
            <form
              className="auth-form"
              onSubmit={handleForgotPassword}
              noValidate
            >
              <TextInput
                id="forgot-email"
                type="email"
                label={t('all.email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                fullBorder
                error={error}
              />

              {success ? <div className="auth-success">{success}</div> : null}

              <Button color="blue" type="submit" loading={isSubmitting}>
                {t('forgotPassword.sendInstructions')}
              </Button>
            </form>
          )}

          <hr className="auth-divider" />
          <Link className="auth-link" to="/login">
            {t('all.login')}
          </Link>
        </div>
      </div>
    </PublicPageContainer>
  )
}

export default ForgotResetPasswordPage
